import i18n from '@/i18n'

/**
 * The agent reports each pipeline node as a `current_step` key; the Figma "Ad Agent" card
 * (492:30607) shows six human steps. Colour measurement belongs to the asset row (it reads
 * the logo), composition to "Writing slogans & CTA", and the final configuration to
 * "Layout & brand colors". Analysing the source material is conditional — a website-only
 * campaign skips it — and the monotonic rule below carries the row straight to done in that
 * case, as it does for "Matching images to slogans" when the agent folds matching into
 * composition.
 *
 * Retired keys from the pre-v8 six-call pipeline are kept as aliases so a job queued before a
 * deploy still lights up a row instead of falling back to the first one.
 */
export const AGENT_DISPLAY_STEPS = [
  { key: 'reading', labelKey: 'editorV2.generation.steps.reading', agentKeys: ['source_intelligence'] },
  {
    key: 'briefing',
    labelKey: 'editorV2.generation.steps.briefing',
    agentKeys: ['source_analysis', 'briefing_context', 'finalize_briefing'],
  },
  {
    key: 'assets',
    labelKey: 'editorV2.generation.steps.assets',
    agentKeys: [
      'asset_preparation',
      'asset_assessment',
      'focal_points',
      'text_removal',
      'color_measurement',
      'brand_colors',
    ],
  },
  {
    key: 'copy',
    labelKey: 'editorV2.generation.steps.copy',
    agentKeys: ['ad_composition', 'slide_planning', 'creative_generation', 'finalize_slide_plan'],
  },
  { key: 'matching', labelKey: 'editorV2.generation.steps.matching', agentKeys: ['slogan_matching'] },
  { key: 'layout', labelKey: 'editorV2.generation.steps.layout', agentKeys: ['layout_selection', 'ad_configuration'] },
]

function normalize(step) {
  return step ? String(step).trim().toLowerCase() : null
}

/**
 * Progress is shown monotonically: the furthest display step any reported key belongs to is
 * active, everything above it is done and everything below is pending. That is what carries a
 * skipped conditional node, and any out-of-order key from an older job, without a row going
 * backwards. On failure the active row turns red.
 *
 * @param {{status?: string, currentStep?: string, steps?: Array<{key: string, state: string}>}|null} job
 * @returns {Array<{key: string, label: string, state: 'pending'|'active'|'done'|'failed'}>}
 */
export function displaySteps(job) {
  const status = job?.status || 'queued'
  const indexOf = (key) => AGENT_DISPLAY_STEPS.findIndex((step) => step.agentKeys.includes(normalize(key)))

  let activeIndex = -1
  for (const step of job?.steps || []) activeIndex = Math.max(activeIndex, indexOf(step.key))
  activeIndex = Math.max(activeIndex, indexOf(job?.currentStep))
  if (activeIndex === -1 && status !== 'completed') activeIndex = 0

  return AGENT_DISPLAY_STEPS.map((step, index) => {
    let state = 'pending'
    if (status === 'completed' || index < activeIndex) state = 'done'
    else if (index === activeIndex) state = status === 'failed' ? 'failed' : 'active'
    return { key: step.key, label: i18n.t(step.labelKey), state }
  })
}

const message = (key, needs = null) => ({ key: `editorV2.generation.agentMessages.${key}`, needs })

/**
 * The live line under the steps: what the agent is doing right now, in plain words, rotating
 * every few seconds while a step runs. `site`, `advertiser` and `pictures` come from what
 * the agent has found so far; a message whose value is not known yet is skipped.
 */
export const AGENT_MESSAGES = {
  reading: [
    message('reading.opening', 'site'),
    message('reading.about', 'advertiser'),
    message('reading.collecting'),
    message('reading.checking'),
  ],
  briefing: [
    message('briefing.reading'),
    message('briefing.offer'),
    message('briefing.pictures'),
  ],
  assets: [
    message('assets.lookingThrough', 'pictures'),
    message('assets.logo'),
    message('assets.photos'),
    message('assets.colours'),
  ],
  copy: [
    message('copy.headlines'),
    message('copy.callToAction'),
    message('copy.short'),
    message('copy.polishing'),
  ],
  matching: [message('matching.photos')],
  layout: [message('layout.colours'), message('layout.together')],
}

/** Usually ready in about this long; after it the card says the run is almost done. */
export const TYPICAL_SECONDS = 45

/**
 * The message to show for the active step, `tick` seconds after the run started (rotating every
 * 3 s). Placeholders are filled from `found`; messages that need an unknown value are skipped.
 *
 * @param {string|null} stepKey a display step key (AGENT_DISPLAY_STEPS)
 * @param {{site?: string, advertiser?: string, pictures?: number}|null} found
 * @param {number} tick
 */
export function agentMessage(stepKey, found, tick) {
  const values = { site: found?.site, advertiser: found?.advertiser, pictures: found?.pictures }
  const usable = (AGENT_MESSAGES[stepKey] || []).filter(({ needs }) => !needs || values[needs])
  if (usable.length === 0) return i18n.t('editorV2.generation.agentMessages.fallback')
  return i18n.t(usable[Math.floor(tick / 3) % usable.length].key, { ...values, count: values.pictures })
}
